/**
 * Settings -> Forms -> a form: the full-page builder. The form's structure
 * (sections, questions and their rules, scoring) sits beside a live preview
 * of the draft as the person answering sees it, under a bar with the
 * published version, the draft's state, Save draft and Publish.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate, useParams } from "react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmationDialog } from "@/admin/components/shared/confirmation-dialog";
import {
  useFormSendFrom,
  useFormTemplates,
  useFormTemplateVersions,
  usePublishFormTemplate,
  useSaveFormTemplate,
  type FormQuestionRef,
  type FormSection,
  type FormSendFrom,
  type FormTemplate,
  type FormTemplateVersion,
} from "@/admin/hooks/use-forms";
import {
  optionsOf,
  typeLabel,
  useFormQuestionBank,
  type Option,
  type Question,
  type QuestionScoring,
} from "@/admin/pages/settings/form-question-bank";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { toastApiError } from "@/shared/lib/api-error";
import { useFormatters } from "@/shared/lib/formatting";
import {
  buildFormFieldFromMeta,
  isConditionallyRequired,
  isFieldVisible,
  normaliseRegistrationField,
  useForm,
  type FormField,
} from "@/shared/lib/forms";
import { cn } from "@/shared/lib/utils";

// "No rule"; a select item cannot have an empty value.
const ALWAYS = "always";

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** The earlier question and answers a rule waits on, in words; null for no rule. */
function ruleParts(
  rule: Record<string, string[]> | undefined,
  bank: Question[],
): { question: string; answers: string } | null {
  const [parent, answers] = Object.entries(rule ?? {})[0] ?? [];
  if (!parent) return null;
  const question = bank.find((q) => q.code_name === parent);
  return {
    question: question?.display_name ?? parent,
    answers: (answers ?? [])
      .map((value) => optionsOf(question).find((o) => o.value === value)?.label ?? value)
      .join(", "),
  };
}

/** "Required when Outcome is Follow up - Shown when ... - Weight 3 - Critical". */
function entryText(entry: FormQuestionRef, bank: Question[], t: TFunction): string {
  const requiredWhen = ruleParts(entry.required_when, bank);
  const shownWhen = ruleParts(entry.visible_when, bank);
  return [
    entry.required
      ? t("forms.settings.required", "Required")
      : requiredWhen
        ? t("forms.settings.requiredWhen", "Required when {{question}} is {{answers}}", requiredWhen)
        : t("forms.settings.optional", "Optional"),
    shownWhen
      ? t("forms.settings.shownWhen", "Shown when {{question}} is {{answers}}", shownWhen)
      : null,
    entry.weight !== undefined && entry.weight !== 1
      ? t("forms.settings.weightIs", "Weight {{weight}}", { weight: entry.weight })
      : null,
    entry.critical ? t("forms.settings.criticalShort", "Critical") : null,
  ]
    .filter(Boolean)
    .join(" - ");
}

/** Takes a question off a form, with any rule that waited on it. */
function withoutEntry(entries: FormQuestionRef[], index: number): FormQuestionRef[] {
  const code = entries[index].code;
  return entries
    .filter((_, i) => i !== index)
    .map((e) => ({
      ...e,
      visible_when: e.visible_when?.[code] ? undefined : e.visible_when,
      required_when: e.required_when?.[code] ? undefined : e.required_when,
    }));
}


/** Whether any question on the form can score or fail. */
function isScored(entries: unknown, scoring: Map<string, QuestionScoring>): boolean {
  return (
    Array.isArray(entries) &&
    (entries as FormQuestionRef[]).some((e) => scoring.has(e.code))
  );
}

/** A key for a new section: its title as a slug, unique on this form. */
function newSectionKey(title: string, sections: FormSection[]): string {
  const base = slugify(title).slice(0, 56) || "section";
  const taken = new Set(sections.map((s) => s.key));
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}_${n}`;
  return key;
}

/** Questions in section order, keeping their order within each section. */
function inSectionOrder(
  entries: FormQuestionRef[],
  sections: FormSection[],
): FormQuestionRef[] {
  const rank = new Map(sections.map((s, i) => [s.key, i]));
  return [...entries].sort(
    (a, b) => (rank.get(a.section ?? "") ?? 0) - (rank.get(b.section ?? "") ?? 0),
  );
}

function moveSection(sections: FormSection[], key: string, step: -1 | 1): FormSection[] {
  const from = sections.findIndex((s) => s.key === key);
  const to = from + step;
  if (from < 0 || to < 0 || to >= sections.length) return sections;
  const next = [...sections];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Takes a section off a form; its questions join the section beside it. */
function withoutSection(
  sections: FormSection[],
  entries: FormQuestionRef[],
  key: string,
): { sections: FormSection[]; entries: FormQuestionRef[] } {
  const at = sections.findIndex((s) => s.key === key);
  const rest = sections.filter((s) => s.key !== key);
  if (rest.length === 0) {
    return { sections: [], entries: entries.map((e) => ({ ...e, section: undefined })) };
  }
  const neighbour = (sections[at - 1] ?? sections[at + 1]).key;
  return {
    sections: rest,
    entries: entries.map((e) => (e.section === key ? { ...e, section: neighbour } : e)),
  };
}

function QuestionRow({
  entry,
  bank,
  answerTypes,
  onEdit,
  onRemove,
}: {
  entry: FormQuestionRef;
  bank: Question[];
  answerTypes: Option[];
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const question = bank.find((q) => q.code_name === entry.code);
  return (
    <li className="flex items-center gap-1 border-b-[0.5px] border-b-[var(--rule)] px-1.5 py-1 last:border-b-0">
      <Button
        type="button"
        variant="ghost"
        onClick={onEdit}
        className="h-auto flex-1 flex-col items-start gap-0.5 whitespace-normal py-1.5 text-left font-normal"
        data-testid={`form-question-${entry.code}`}
      >
        <span className="text-[13px] text-[var(--ink)]">
          {question?.display_name ?? entry.code}
        </span>
        <span className="text-[11px] text-[var(--ink-3)]">{entryText(entry, bank, t)}</span>
      </Button>
      {question ? (
        <Badge variant="outline" className="shrink-0 font-normal">
          {typeLabel(answerTypes, question.field_type)}
        </Badge>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onRemove}
        aria-label={t("forms.settings.removeFromForm", "Take off this form")}
      >
        <X size={14} />
      </Button>
    </li>
  );
}

function FormQuestionList({
  entries,
  sections,
  bank,
  answerTypes,
  onAdd,
  onEdit,
  onRemove,
  onAddSection,
  onEditSection,
  onMoveSection,
  onRemoveSection,
}: {
  entries: FormQuestionRef[];
  sections: FormSection[];
  bank: Question[];
  answerTypes: Option[];
  onAdd: (section?: string) => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onAddSection: () => void;
  onEditSection: (key: string) => void;
  onMoveSection: (key: string, step: -1 | 1) => void;
  onRemoveSection: (key: string) => void;
}) {
  const { t } = useTranslation();
  const canAdd = bank.some((q) => !entries.some((e) => e.code === q.code_name));
  const row = (entry: FormQuestionRef, index: number) => (
    <QuestionRow
      key={entry.code}
      entry={entry}
      bank={bank}
      answerTypes={answerTypes}
      onEdit={() => onEdit(index)}
      onRemove={() => onRemove(index)}
    />
  );
  return (
    <div className="flex flex-col gap-2" data-testid="form-question-list">
      <Label className="block clara-label font-normal">
        {t("forms.settings.questions", "Questions")}
      </Label>
      {sections.length > 0 ? (
        <div className="flex flex-col gap-3">
          {sections.map((section, position) => {
            const items = entries
              .map((entry, index) => ({ entry, index }))
              .filter(({ entry }) => entry.section === section.key);
            return (
              <div
                key={section.key}
                className="rounded-lg border-[0.5px] border-[var(--rule)]"
                data-testid={`form-section-${section.key}`}
              >
                <div className="flex items-start gap-1 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-light)] px-3 py-2">
                  <div className="min-w-0 flex-1 py-1">
                    <p className="text-[13px] font-medium text-[var(--ink)]">
                      {section.title}
                    </p>
                    {section.description ? (
                      <p className="text-[11px] text-[var(--ink-3)]">{section.description}</p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onEditSection(section.key)}
                    aria-label={t("forms.settings.editSection", "Edit section")}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onMoveSection(section.key, -1)}
                    disabled={position === 0}
                    aria-label={t("forms.settings.moveSectionUp", "Move section up")}
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onMoveSection(section.key, 1)}
                    disabled={position === sections.length - 1}
                    aria-label={t("forms.settings.moveSectionDown", "Move section down")}
                  >
                    <ArrowDown size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onRemoveSection(section.key)}
                    aria-label={t("forms.settings.removeSection", "Remove section")}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                {items.length === 0 ? (
                  <p className="px-3 py-2 text-[12px] text-[var(--ink-3)]">
                    {t("forms.settings.noSectionQuestions", "No questions in this section yet.")}
                  </p>
                ) : (
                  <ol className="flex flex-col">
                    {items.map(({ entry, index }) => row(entry, index))}
                  </ol>
                )}
                {canAdd ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="m-1"
                    onClick={() => onAdd(section.key)}
                    data-testid={`form-add-question-${section.key}`}
                  >
                    <Plus size={14} />
                    {t("forms.settings.addQuestion", "Add question")}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : entries.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t("forms.settings.noFormQuestions", "No questions on this form yet.")}
        </p>
      ) : (
        <div className="rounded-lg border-[0.5px] border-[var(--rule)]">
          <ol className="flex flex-col">{entries.map(row)}</ol>
        </div>
      )}
      {bank.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t("forms.settings.bankEmpty", "Create questions under Questions first.")}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {canAdd && sections.length === 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAdd()}
              data-testid="form-add-question"
            >
              <Plus size={14} />
              {t("forms.settings.addQuestion", "Add question")}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddSection}
            data-testid="form-add-section"
          >
            <Plus size={14} />
            {t("forms.settings.addSection", "Add section")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Adds a question to a form, or edits how one already on it is asked. */
function FormQuestionPicker({
  bank,
  scoring,
  entries,
  sections,
  index,
  section,
  onSave,
  onClose,
}: {
  bank: Question[];
  scoring: Map<string, QuestionScoring>;
  entries: FormQuestionRef[];
  sections: FormSection[];
  index: number | null;
  /** The section a new question goes into. */
  section?: string;
  onSave: (entry: FormQuestionRef) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const editing = index === null ? undefined : entries[index];

  const fields = useMemo<FormField[]>(() => {
    const onForm = new Set(entries.map((e) => e.code));
    const choices = bank.filter(
      (q) => q.code_name === editing?.code || !onForm.has(q.code_name),
    );
    // A question can wait on an earlier "pick one" question on this form. A
    // new one lands at the end of its section.
    const rank = new Map(sections.map((s, i) => [s.key, i]));
    const before =
      index !== null
        ? entries.slice(0, index)
        : section === undefined
          ? entries
          : entries.filter(
              (e) => (rank.get(e.section ?? "") ?? 0) <= (rank.get(section) ?? 0),
            );
    const earlier = before
      .map((e) => bank.find((q) => q.code_name === e.code))
      .filter((q): q is Question => q?.field_type === "SELECT");
    const [parent, answers] = Object.entries(editing?.visible_when ?? {})[0] ?? [
      ALWAYS,
      [],
    ];
    const [requiredParent, requiredAnswers] = Object.entries(
      editing?.required_when ?? {},
    )[0] ?? [ALWAYS, []];
    const meaning = (ctx: { values: Record<string, unknown> }) =>
      scoring.get(String(ctx.values.code ?? ""));
    return [
      {
        type: "select",
        name: "code",
        label: t("forms.settings.colQuestion", "Question"),
        value: editing?.code ?? "",
        required: t("forms.settings.pickQuestionRequired", "Pick a question"),
        options: choices.map((q) => ({ value: q.code_name, label: q.display_name })),
        configurationData: { searchable: true },
        disabled: Boolean(editing),
        testId: "form-question-pick",
      },
      ...(editing && sections.length > 0
        ? [
            {
              type: "select" as const,
              name: "section",
              label: t("forms.settings.section", "Section"),
              value: editing.section ?? sections[0].key,
              options: sections.map((s) => ({ value: s.key, label: s.title })),
              testId: "form-question-section",
            },
          ]
        : []),
      {
        type: "checkbox",
        name: "required",
        label: t("forms.settings.mustAnswer", "Must be answered"),
        value: Boolean(editing?.required || editing?.required_when),
      },
      {
        type: "select",
        name: "required_parent",
        label: t("forms.settings.requiredWhenLabel", "Only required when"),
        value: requiredParent,
        options: [
          { value: ALWAYS, label: t("forms.settings.requiredAlways", "Always required") },
          ...earlier.map((q) => ({ value: q.code_name, label: q.display_name })),
        ],
        visible: (ctx) => Boolean(ctx.values.required) && earlier.length > 0,
        testId: "form-question-required-parent",
      },
      ...earlier.map(
        (q): FormField => ({
          type: "multiSelect",
          name: `required_answers__${q.code_name}`,
          label: t("forms.settings.showAnswers", "Is one of"),
          placeholder: t("forms.settings.showAnswersPlaceholder", "Pick answers"),
          value: q.code_name === requiredParent ? requiredAnswers : [],
          required: t("forms.settings.showAnswersRequired", "Pick at least one answer"),
          options: optionsOf(q),
          visible: (ctx) =>
            Boolean(ctx.values.required) && ctx.values.required_parent === q.code_name,
          testId: `form-question-required-answers-${q.code_name}`,
        }),
      ),
      {
        type: "select",
        name: "parent",
        label: t("forms.settings.showWhen", "Only show when"),
        value: parent,
        options: [
          { value: ALWAYS, label: t("forms.settings.showAlways", "Always show") },
          ...earlier.map((q) => ({ value: q.code_name, label: q.display_name })),
        ],
        visible: earlier.length > 0,
        testId: "form-question-parent",
      },
      ...earlier.map(
        (q): FormField => ({
          type: "multiSelect",
          name: `answers__${q.code_name}`,
          label: t("forms.settings.showAnswers", "Is one of"),
          placeholder: t("forms.settings.showAnswersPlaceholder", "Pick answers"),
          value: q.code_name === parent ? answers : [],
          required: t("forms.settings.showAnswersRequired", "Pick at least one answer"),
          options: optionsOf(q),
          visible: (ctx) => ctx.values.parent === q.code_name,
          testId: `form-question-answers-${q.code_name}`,
        }),
      ),
      {
        type: "number",
        name: "weight",
        label: t("forms.settings.weight", "Weight"),
        hint: t("forms.settings.weightHint", "Its score counts this many times."),
        value: editing?.weight ?? 1,
        validationSchema: z
          .union([z.number(), z.string()])
          .optional()
          .refine(
            (value) =>
              value === undefined ||
              value === "" ||
              (Number(value) >= 0 && Number(value) <= 100),
            t("forms.settings.weightRange", "Enter a weight from 0 to 100"),
          ),
        visible: (ctx) => Boolean(meaning(ctx)?.scored),
        testId: "form-question-weight",
      },
      {
        type: "checkbox",
        name: "critical",
        label: t("forms.settings.critical", "Critical: a failed answer fails the form"),
        value: editing?.critical ?? false,
        visible: (ctx) => Boolean(meaning(ctx)?.canFail),
        testId: "form-question-critical",
      },
    ];
  }, [bank, scoring, entries, sections, editing, index, section, t]);

  const { Form, ref } = useForm(
    { name: "form-question-picker", submit: () => <></>, fields },
    async (values) => {
      const required = Boolean(values.required);
      const requiredParent = String(values.required_parent ?? ALWAYS);
      const entry: FormQuestionRef = {
        code: String(values.code),
        required: required && requiredParent === ALWAYS,
      };
      if (required && requiredParent !== ALWAYS) {
        entry.required_when = {
          [requiredParent]: values[`required_answers__${requiredParent}`] as string[],
        };
      }
      const meaning = scoring.get(entry.code);
      const weight = Number(values.weight);
      if (meaning?.scored && values.weight !== "" && weight !== 1) entry.weight = weight;
      if (meaning?.canFail && values.critical) entry.critical = true;
      if (sections.length > 0) {
        entry.section = String(
          values.section ?? section ?? editing?.section ?? sections[0].key,
        );
      }
      const parent = String(values.parent ?? ALWAYS);
      if (parent !== ALWAYS) {
        entry.visible_when = { [parent]: values[`answers__${parent}`] as string[] };
      }
      onSave(entry);
    },
    [bank, scoring, entries, sections, index, section, t],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]" data-testid="form-question-picker">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t("forms.settings.editOnForm", "Question on this form")
              : t("forms.settings.addToForm", "Add a question")}
          </DialogTitle>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            data-testid="form-question-picker-save"
          >
            {editing ? t("common.save", "Save") : t("common.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Adds a section to a form, or renames and describes one. */
function FormSectionDialog({
  section,
  onSave,
  onClose,
}: {
  section?: FormSection;
  onSave: (values: { title: string; description: string }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "title",
        label: t("forms.settings.sectionTitle", "Section title"),
        required: t("forms.settings.sectionTitleRequired", "Give the section a title"),
        value: section?.title ?? "",
        placeholder: t("forms.settings.sectionTitlePlaceholder", "e.g. Roof"),
        maxLength: 120,
        testId: "form-section-title",
      },
      {
        type: "textarea",
        name: "description",
        label: t("forms.settings.description", "Description"),
        value: section?.description ?? "",
      },
    ],
    [section, t],
  );
  const { Form, ref } = useForm(
    { name: "form-section", submit: () => <></>, fields },
    async (values) => {
      onSave({
        title: String(values.title || "").trim(),
        description: String(values.description || "").trim(),
      });
    },
    [section, t],
  );
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[440px]" data-testid="form-section-dialog">
        <DialogHeader>
          <DialogTitle>
            {section
              ? t("forms.settings.editSection", "Edit section")
              : t("forms.settings.newSection", "New section")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "forms.settings.sectionHint",
              "The person filling in the form sees one section at a time.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button onClick={() => ref.current?.submitForm()} data-testid="form-section-save">
            {section ? t("common.save", "Save") : t("common.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** JSON with object keys sorted, so equal values match whatever order their keys came in. */
function sameJson(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
      : v,
  );
}

/** The template write the builder's values make. */
function toWrite(values: Record<string, unknown>) {
  const passMark = values.pass_mark;
  return {
    name: String(values.name || "").trim(),
    description: String(values.description || ""),
    is_active: Boolean(values.is_active),
    record_types: (values.record_types ?? []) as string[],
    questions: (values.questions ?? []) as FormQuestionRef[],
    sections: (values.sections ?? []) as FormSection[],
    scoring: {
      ...(passMark === "" || passMark == null ? {} : { pass_mark: Number(passMark) }),
      ...(values.critical_fails === false ? { critical_fails: false } : {}),
    },
  };
}

/** Hands the form's live values up to the page, for the bar and the preview. */
function ValuesBridge({
  values,
  onChange,
}: {
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}) {
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
}

/** The draft as the person answering sees it: rules react and sections step, as on the link page. */
function FormPreview({
  name,
  description,
  entries,
  sections,
  bank,
}: {
  name: string;
  description: string;
  entries: FormQuestionRef[];
  sections: FormSection[];
  bank: Question[];
}) {
  const { t } = useTranslation();
  // Sections that ask something, in order. With two or more, one shows at a time.
  const shown = useMemo(() => {
    const used = new Set(entries.map((e) => e.section ?? ""));
    return sections.filter((s) => used.has(s.key));
  }, [entries, sections]);
  const stepped = shown.length > 1;
  const [current, setCurrent] = useState(0);
  // Read by each field's `visible` check, which the form evaluates on render.
  const currentRef = useRef(0);
  const [checked, setChecked] = useState(false);
  // Moving or removing sections can leave the open one past the end.
  const at = Math.min(current, Math.max(shown.length - 1, 0));

  const fields = useMemo(
    () =>
      entries.flatMap((entry, order): FormField[] => {
        const question = bank.find((q) => q.code_name === entry.code);
        if (!question) return [];
        // The form's rules stand in for any on the question, as when it is sent.
        const config = Object.fromEntries(
          Object.entries(question.configuration_data ?? {}).filter(
            ([key]) => key !== "visible_when" && key !== "required_when",
          ),
        );
        const field = buildFormFieldFromMeta(
          normaliseRegistrationField({
            uuid: question.uuid,
            code_name: question.code_name,
            display_name: question.display_name,
            field_type: question.field_type,
            required: entry.required,
            hint: question.hint,
            description: question.description,
            options: optionsOf(question),
            validation_rules: (config.validation_rules ?? []) as never,
            configuration_data: {
              ...config,
              ...(entry.visible_when ? { visible_when: entry.visible_when } : {}),
              ...(entry.required_when ? { required_when: entry.required_when } : {}),
            },
            order,
          }),
          undefined,
          "submit",
        );
        if (!stepped) return [field];
        const key = entry.section ?? "";
        return [{ ...field, visible: () => shown[currentRef.current]?.key === key }];
      }),
    [entries, bank, shown, stepped],
  );

  const { Form, ref } = useForm(
    {
      name: "form-builder-preview",
      submit: () => <></>,
      fields,
      // Photos stay in the browser: nothing in the preview is saved.
      uploadFile: async ({ file }) => ({
        uuid: crypto.randomUUID(),
        file_name: file.name,
        file_url: URL.createObjectURL(file),
      }),
    },
    async () => undefined,
    // The `visible` checks read the sections; JSON cannot see into them.
    [shown],
  );

  useEffect(() => {
    currentRef.current = at;
    if (at !== current) setCurrent(at);
  }, [at, current]);
  useEffect(() => setChecked(false), [fields]);

  const goTo = (index: number) => {
    currentRef.current = index;
    setCurrent(index);
    setChecked(false);
  };
  const last = !stepped || at === shown.length - 1;
  const next = async () => {
    const form = ref.current;
    if (!form) return;
    // Checks the open section only: the others are hidden.
    await form.submitForm();
    if (Object.keys(await form.validateForm()).length > 0) return;
    // Required-when is enforced by the server when a sent form is submitted;
    // the preview has no server, so it checks the open section's here.
    const values = form.values;
    const missing = entries.filter((entry) => {
      const value = values[entry.code];
      return (
        (!stepped || (entry.section ?? "") === shown[at]?.key) &&
        isFieldVisible({ visible_when: entry.visible_when }, values) &&
        isConditionallyRequired({ required_when: entry.required_when }, values) &&
        (value == null || value === "" || (Array.isArray(value) && value.length === 0))
      );
    });
    for (const entry of missing) {
      const label = bank.find((q) => q.code_name === entry.code)?.display_name ?? entry.code;
      form.setFieldError(
        entry.code,
        t("forms.builder.answerRequired", "{{label}} is required", { label }),
      );
    }
    if (missing.length > 0) return;
    if (last) setChecked(true);
    else goTo(at + 1);
  };
  const section = stepped ? shown[at] : shown[0];

  return (
    <div data-testid="form-preview">
      <h3 className="clara-heading text-[20px]">
        {name || t("forms.settings.newForm", "New form")}
      </h3>
      {description ? (
        <p className="clara-body mt-1 text-[var(--ink-2)]">{description}</p>
      ) : null}
      {fields.length === 0 ? (
        <p className="mt-6 text-[13px] text-[var(--ink-3)]">
          {t("forms.builder.previewEmpty", "Add a question to see the form here.")}
        </p>
      ) : (
        <div className="mt-6">
          {section ? (
            <div className="mb-4" data-testid="form-preview-section">
              {stepped ? (
                <p className="clara-small mb-1 uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t("upload.form.sectionOf", "Section {{current}} of {{total}}", {
                    current: at + 1,
                    total: shown.length,
                  })}
                </p>
              ) : null}
              <h4 className="clara-heading text-[18px]">{section.title}</h4>
              {section.description ? (
                <p className="clara-body mt-1 text-[var(--ink-2)]">{section.description}</p>
              ) : null}
            </div>
          ) : null}
          <Form className="flex flex-col gap-3" />
          <div className="mt-5 flex items-center gap-3">
            {stepped && at > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => goTo(at - 1)}
                data-testid="form-preview-back"
              >
                <ArrowLeft size={14} />
                {t("common.back", "Back")}
              </Button>
            ) : null}
            <Button
              type="button"
              className="ml-auto"
              onClick={() => void next()}
              data-testid="form-preview-next"
            >
              {last ? (
                t("forms.builder.checkAnswers", "Check answers")
              ) : (
                <>
                  {t("common.next", "Next")}
                  <ArrowRight size={14} />
                </>
              )}
            </Button>
          </div>
          {checked ? (
            <p className="mt-3 text-[12px] text-[var(--ink-3)]" data-testid="form-preview-checked">
              {t("forms.builder.checked", "Every required question is answered.")}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The versions sends have used, newest first: a list, so it fits beside the preview. */
function FormVersions({ templateKey }: { templateKey: string }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const versionsQuery = useFormTemplateVersions(templateKey);
  const versions = versionsQuery.data ?? [];
  return (
    <section data-testid="form-versions">
      <h2 className="clara-label mb-3">{t("forms.builder.versions", "Published versions")}</h2>
      {versionsQuery.isPending ? (
        <p className="text-[12px] text-[var(--ink-3)]">{t("common.loading", "Loading...")}</p>
      ) : versions.length === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">
          {t("forms.builder.versionsEmpty", "Not published yet.")}
        </p>
      ) : (
        <ol className="max-h-[320px] overflow-y-auto rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
          {versions.map((version: FormTemplateVersion) => (
            <li
              key={version.number}
              className="border-b-[0.5px] border-b-[var(--rule)] px-4 py-2.5 last:border-b-0"
              data-testid="form-version"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-medium text-[var(--ink)]">
                  {t("forms.settings.versionN", "Version {{number}}", { number: version.number })}
                </span>
                <span className="shrink-0 text-[12px] text-[var(--ink-3)]">
                  {t("forms.builder.questionCount", { count: version.question_count })}
                </span>
              </div>
              <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
                {[version.name, formatDateTime(version.published_at), version.published_by]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

type Device = "phone" | "desktop";

function FormEditor({
  template,
  bank,
  scoring,
  answerTypes,
  sendFrom,
}: {
  template?: FormTemplate;
  bank: Question[];
  scoring: Map<string, QuestionScoring>;
  answerTypes: Option[];
  /** The records this tenant can send forms from. */
  sendFrom: FormSendFrom[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveFormTemplate();
  const publish = usePublishFormTemplate();
  // The form's questions and sections as they were when the picker opened.
  const [picker, setPicker] = useState<{
    index: number | null;
    entries: FormQuestionRef[];
    sections: FormSection[];
    section?: string;
  } | null>(null);
  // The section being edited (none: adding one), as it was when opened.
  const [sectionDialog, setSectionDialog] = useState<{
    key: string | null;
    section?: FormSection;
  } | null>(null);
  const [device, setDevice] = useState<Device>("phone");
  const [leaving, setLeaving] = useState(false);

  // What the form opened with: the fields start here and "unsaved" compares with it.
  const initial = useMemo(
    () => ({
      name: template?.name ?? "",
      description: template?.description ?? "",
      is_active: template?.is_active ?? true,
      // A new form starts sendable from the first kind of record offered.
      record_types: template?.record_types ?? sendFrom.slice(0, 1).map((c) => c.value),
      sections: template?.sections ?? [],
      // A question deleted from the bank since is left off.
      questions: (template?.questions ?? []).filter((e) =>
        bank.some((q) => q.code_name === e.code),
      ),
      pass_mark: template?.scoring?.pass_mark ?? "",
      critical_fails: template?.scoring?.critical_fails ?? true,
    }),
    [template, bank, sendFrom],
  );
  const [values, setValues] = useState<Record<string, unknown>>(initial);
  const report = useCallback(
    (next: Record<string, unknown>) =>
      setValues((prev) => (sameJson(prev) === sameJson(next) ? prev : next)),
    [],
  );
  const dirty = sameJson(toWrite(values)) !== sameJson(toWrite(initial));

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "name",
        label: t("forms.settings.name", "Name"),
        required: t("forms.settings.nameRequired", "Name is required"),
        value: initial.name,
        placeholder: t("forms.settings.namePlaceholder", "e.g. Site survey"),
      },
      {
        type: "textarea",
        name: "description",
        label: t("forms.settings.description", "Description"),
        value: initial.description,
      },
      {
        type: "switch",
        name: "is_active",
        label: t("forms.settings.active", "Active"),
        hint: t("forms.settings.activeHint", "Only active forms can be sent."),
        value: initial.is_active,
      },
      {
        type: "multiSelect",
        name: "record_types",
        label: t("forms.builder.sendFrom", "Can be sent from"),
        placeholder: t("forms.builder.sendFromPlaceholder", "Pick records"),
        value: initial.record_types,
        required: t("forms.builder.sendFromRequired", "Pick where this form can be sent from"),
        // One the tenant no longer has stays listed, so saving never drops it unseen.
        options: [
          ...sendFrom,
          ...initial.record_types
            .filter((value) => !sendFrom.some((c) => c.value === value))
            .map((value) => ({ value, label: value })),
        ],
        testId: "form-send-from",
      },
      {
        // Held here, edited through the question list below.
        type: "custom",
        name: "sections",
        value: initial.sections,
        visible: false,
        render: () => <></>,
      },
      {
        type: "custom",
        name: "questions",
        label: t("forms.settings.questions", "Questions"),
        value: initial.questions,
        render: (ctx) => {
          const entries = (ctx.values.questions ?? []) as FormQuestionRef[];
          const sections = (ctx.values.sections ?? []) as FormSection[];
          const restructure = (next: {
            sections: FormSection[];
            entries: FormQuestionRef[];
          }) => {
            ctx.setFieldValue("sections", next.sections);
            ctx.setFieldValue("questions", inSectionOrder(next.entries, next.sections));
          };
          return (
            <FormQuestionList
              entries={entries}
              sections={sections}
              bank={bank}
              answerTypes={answerTypes}
              onAdd={(section) => setPicker({ index: null, entries, sections, section })}
              onEdit={(index) => setPicker({ index, entries, sections })}
              onRemove={(index) => ctx.setFieldValue("questions", withoutEntry(entries, index))}
              onAddSection={() => setSectionDialog({ key: null })}
              onEditSection={(key) =>
                setSectionDialog({ key, section: sections.find((s) => s.key === key) })
              }
              onMoveSection={(key, step) =>
                restructure({ sections: moveSection(sections, key, step), entries })
              }
              onRemoveSection={(key) => restructure(withoutSection(sections, entries, key))}
            />
          );
        },
      },
      {
        type: "number",
        name: "pass_mark",
        label: t("forms.settings.passMark", "Pass mark (%)"),
        hint: t(
          "forms.settings.passMarkHint",
          "Leave empty to pass or fail on critical questions alone.",
        ),
        value: initial.pass_mark,
        validationSchema: z
          .union([z.number(), z.string()])
          .optional()
          .refine(
            (value) =>
              value === undefined ||
              value === "" ||
              (Number(value) >= 0 && Number(value) <= 100),
            t("forms.settings.passMarkRange", "Enter a percentage from 0 to 100"),
          ),
        visible: (ctx) => isScored(ctx.values.questions, scoring),
        testId: "form-pass-mark",
      },
      {
        type: "switch",
        name: "critical_fails",
        label: t("forms.settings.criticalFails", "A failed critical question fails the form"),
        value: initial.critical_fails,
        visible: (ctx) => isScored(ctx.values.questions, scoring),
        testId: "form-critical-fails",
      },
    ],
    [initial, bank, scoring, answerTypes, sendFrom, t],
  );

  // Which bar button submitted: saving keeps a draft, publishing freezes it.
  const publishing = useRef(false);
  const { Form, ref } = useForm(
    { name: "form-template", submit: () => <></>, fields },
    async (formValues) => {
      const write = toWrite(formValues);
      let saved: FormTemplate;
      try {
        saved = await save.mutateAsync({
          isNew: !template,
          key: template?.key ?? slugify(write.name),
          ...write,
        });
      } catch (err) {
        toastApiError(err, t("forms.settings.saveFailed", "Could not save the form."));
        return;
      }
      if (!publishing.current) {
        toast.success(t("forms.settings.draftSaved", "Draft saved"));
      } else {
        try {
          const published = await publish.mutateAsync(saved.key);
          const number = published.published_version;
          toast.success(
            number === template?.published_version
              ? t("forms.builder.alreadyPublished", "Version {{number}} is already published", {
                  number,
                })
              : t("forms.settings.published", "Published as version {{number}}", { number }),
          );
        } catch (err) {
          toastApiError(
            err,
            t("forms.settings.publishFailed", "Draft saved, but it could not be published."),
          );
        }
      }
      // A new form moves to its own address once it exists.
      if (!template) navigate(`/settings/forms/${saved.key}`, { replace: true });
    },
    [bank, scoring, answerTypes, t],
  );
  const busy = save.isPending || publish.isPending;
  const submitAs = (andPublish: boolean) => {
    publishing.current = andPublish;
    ref.current?.submitForm();
  };
  const back = () => navigate("/settings/forms");

  const currentSections = () => (ref.current?.values.sections ?? []) as FormSection[];

  const pick = (entry: FormQuestionRef) => {
    if (!picker) return;
    const next = [...picker.entries];
    if (picker.index === null) next.push(entry);
    else next[picker.index] = entry;
    ref.current?.setFieldValue("questions", inSectionOrder(next, currentSections()));
    setPicker(null);
  };

  const saveSection = (section: { title: string; description: string }) => {
    if (!sectionDialog) return;
    const sections = currentSections();
    if (sectionDialog.key) {
      ref.current?.setFieldValue(
        "sections",
        sections.map((s) => (s.key === sectionDialog.key ? { ...s, ...section } : s)),
      );
    } else {
      const added = { key: newSectionKey(section.title, sections), ...section };
      const entries = (ref.current?.values.questions ?? []) as FormQuestionRef[];
      ref.current?.setFieldValue("sections", [...sections, added]);
      // The first section takes every question already on the form.
      if (sections.length === 0) {
        ref.current?.setFieldValue(
          "questions",
          entries.map((e) => ({ ...e, section: added.key })),
        );
      }
    }
    setSectionDialog(null);
  };

  const published = template?.published_version ?? null;
  return (
    <div className="pb-16" data-testid="form-builder">
      <div
        // On desktop it pulls up over the pane's padding, with a strip above
        // it so nothing scrolls through the gap; on a phone it sits below
        // the "Back to settings" link instead of covering it.
        className="sticky top-0 z-20 -mx-4 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 lg:-mx-8 lg:-mt-6 lg:px-8 lg:py-4 lg:before:absolute lg:before:inset-x-0 lg:before:bottom-full lg:before:h-6 lg:before:bg-[var(--bg)] lg:before:content-['']"
        data-testid="form-builder-bar"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => (dirty ? setLeaving(true) : back())}
              aria-label={t("forms.builder.backToForms", "Back to forms")}
              data-testid="form-builder-back"
            >
              <ArrowLeft size={16} />
            </Button>
            <div className="min-w-0">
              <h1 className="truncate text-[15px] font-medium text-[var(--ink)]">
                {String(values.name || "") || t("forms.settings.newForm", "New form")}
              </h1>
              <div
                className="mt-1 flex flex-wrap items-center gap-1.5"
                data-testid="form-builder-state"
              >
                {published === null ? (
                  <Badge variant="outline">
                    {t("forms.settings.notPublished", "Not published")}
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    {t("forms.builder.versionPublished", "Version {{number}} published", {
                      number: published,
                    })}
                  </Badge>
                )}
                {dirty ? (
                  <Badge variant="outline">{t("forms.builder.unsaved", "Unsaved changes")}</Badge>
                ) : published !== null && template?.has_draft_changes ? (
                  <Badge variant="outline">
                    {t("forms.settings.draftChanges", "Draft changes")}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => submitAs(false)}
              disabled={busy}
              data-testid="form-template-save"
            >
              {t("forms.settings.saveDraft", "Save draft")}
            </Button>
            <Button
              onClick={() => submitAs(true)}
              disabled={busy}
              data-testid="form-template-publish"
            >
              {busy
                ? t("forms.settings.saving", "Saving...")
                : t("forms.settings.publish", "Publish")}
            </Button>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "mt-6 grid grid-cols-1 gap-8",
          device === "phone"
            ? "xl:grid-cols-[minmax(0,1fr)_420px]"
            : "xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]",
        )}
      >
        <div className="flex min-w-0 flex-col gap-8">
          <section
            className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-5"
            data-testid="form-builder-structure"
          >
            <Form className="flex flex-col gap-4">
              {({ formContext }) => (
                <ValuesBridge values={formContext.values} onChange={report} />
              )}
            </Form>
          </section>
          {template ? <FormVersions templateKey={template.key} /> : null}
        </div>

        <aside
          className="min-w-0 xl:sticky xl:top-24 xl:self-start"
          data-testid="form-builder-preview"
        >
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="clara-label">{t("forms.builder.preview", "Preview")}</h2>
              <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                {t("forms.builder.previewHint", "Answers here are not saved.")}
              </p>
            </div>
            <Tabs
              value={device}
              onValueChange={(value) => setDevice(value as Device)}
              className="hidden xl:flex"
            >
              <TabsList variant="pill">
                <TabsTrigger value="phone" data-testid="form-preview-phone">
                  {t("forms.builder.phone", "Phone")}
                </TabsTrigger>
                <TabsTrigger value="desktop" data-testid="form-preview-desktop">
                  {t("forms.builder.desktop", "Desktop")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div
            className={cn(
              "overflow-y-auto border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] xl:max-h-[calc(100vh-380px)]",
              device === "phone"
                ? "mx-auto w-full max-w-[390px] rounded-[28px] px-5 py-6"
                : "w-full rounded-lg p-8",
            )}
            data-testid={`form-preview-frame-${device}`}
          >
            <FormPreview
              name={String(values.name || "")}
              description={String(values.description || "")}
              entries={(values.questions ?? []) as FormQuestionRef[]}
              sections={(values.sections ?? []) as FormSection[]}
              bank={bank}
            />
          </div>
        </aside>
      </div>

      {picker ? (
        <FormQuestionPicker
          bank={bank}
          scoring={scoring}
          entries={picker.entries}
          sections={picker.sections}
          index={picker.index}
          section={picker.section}
          onSave={pick}
          onClose={() => setPicker(null)}
        />
      ) : null}
      {sectionDialog ? (
        <FormSectionDialog
          section={sectionDialog.section}
          onSave={saveSection}
          onClose={() => setSectionDialog(null)}
        />
      ) : null}
      <ConfirmationDialog
        open={leaving}
        onOpenChange={setLeaving}
        title={t("forms.builder.leaveTitle", "Leave without saving?")}
        description={t("forms.builder.leaveBody", "Your changes to this form will be lost.")}
        confirmLabel={t("forms.builder.leave", "Leave")}
        cancelLabel={t("common.cancel", "Cancel")}
        destructive
        onConfirm={back}
      />
    </div>
  );
}

/** Settings -> Forms -> a form (or "new"): loads the form and its question bank, then the editor. */
export function FormBuilderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { key } = useParams<{ key: string }>();
  const isNew = key === "new";
  const templatesQuery = useFormTemplates();
  const sendFromQuery = useFormSendFrom();
  const { entityQuery, setsQuery, questions, scoring, answerTypes } = useFormQuestionBank();
  const template = isNew ? undefined : templatesQuery.data?.find((tpl) => tpl.key === key);

  // The editor starts from the bank, so it waits for it: a question missing
  // from a half-loaded bank would be dropped from the form on save.
  if (
    entityQuery.isConfigLoading ||
    setsQuery.isPending ||
    sendFromQuery.isPending ||
    (!isNew && !template && (templatesQuery.isPending || templatesQuery.isFetching))
  ) {
    return (
      <p
        className="py-16 text-center text-[13px] text-[var(--ink-3)]"
        data-testid="form-builder-loading"
      >
        {t("common.loading", "Loading...")}
      </p>
    );
  }
  if (!isNew && !template) {
    return (
      <div className="py-16 text-center" data-testid="form-builder-not-found">
        <p className="text-[13px] text-[var(--ink-3)]">
          {t("forms.builder.notFound", "This form doesn't exist.")}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/settings/forms")}>
          {t("forms.builder.backToForms", "Back to forms")}
        </Button>
      </div>
    );
  }
  return (
    <FormEditor
      key={template?.uuid ?? "new"}
      template={template}
      bank={questions}
      scoring={scoring}
      answerTypes={answerTypes}
      sendFrom={sendFromQuery.data ?? []}
    />
  );
}

export default FormBuilderPage;

